import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { ThemeProvider } from "./context/ThemeContext";
import { BookingProvider } from "./context/BookingContext";
import { GooglePlacesProvider } from "./context/GooglePlacesContext";
import Header from "./components/layout/Header";
import ContactForm from "./components/forms/ContactForm";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { initializeSession } from "./utils/cookies";
import { analytics } from "./utils/analytics";
import { databaseService } from "./services/databaseService";
import { userJourneyService } from "./services/userJourneyService";
import { preloadCriticalComponents } from "./utils/dynamicImports";
import { PerformanceProfiler } from "./utils/performanceProfiler";
import {
  usePerformanceMonitoring,
  trackBundleSize,
} from "./utils/performanceUtils";
import "./services/utmPromoService"; // Initialize UTM promo service
import Spinner from "./components/common/Spinner";
import SessionMonitor from "./components/SessionMonitor";

// Lazy load heavy components
const MeasurementForm = lazy(
  () => import("./components/forms/MeasurementForm")
);
const FileUploadPage = lazy(
  () => import("./components/booking/FileUploadPage")
);
const BookingCalendar = lazy(
  () => import("./components/booking/BookingCalendar")
);
const ConfirmationPage = lazy(
  () => import("./components/booking/ConfirmationPage")
);
const PriceEstimationPage = lazy(
  () => import("./components/booking/PriceEstimationPage")
);
const SettingsPage = lazy(() => import("./components/admin/SettingsPage"));
const AnalyticsDashboard = lazy(
  () => import("./components/admin/AnalyticsDashboard")
);
const LeadsDashboard = lazy(
  () => import("./components/dashboard/LeadsDashboard")
);
const TrakSettingsPanel = lazy(
  () => import("./components/booking/TrakSettingsPanel")
);
const RescheduleBookingPage = lazy(
  () => import("./components/booking/RescheduleBookingPage")
);
const PromotionalBannerComponent = lazy(
  () => import("./components/common/PromotionalBanner")
);

console.log("Eclipse Booking Application - Optimized loading!");

// Optimized QueryClient for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1, // Reduce retries for faster failures
      refetchOnWindowFocus: false, // Reduce unnecessary refetches
    },
    mutations: {
      retry: 1,
    },
  },
});

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Application error:", error, errorInfo);
    analytics.trackError("app_error", error.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We're sorry, but something unexpected happened. Please refresh the
              page to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  // Enable performance monitoring
  usePerformanceMonitoring();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        PerformanceProfiler.mark("app-init-start");

        // Initialize session and capture UTM parameters
        PerformanceProfiler.mark("session-init-start");
        initializeSession();
        PerformanceProfiler.measure("session-init", "session-init-start");

        // Initialize user journey tracking
        PerformanceProfiler.mark("user-journey-start");
        userJourneyService.initializeJourney();
        userJourneyService.recordStep("app_start", {
          timestamp: new Date().toISOString(),
          url: window.location.href,
          referrer: document.referrer || "direct",
        });
        PerformanceProfiler.measure("user-journey-init", "user-journey-start");

        // Track app start
        PerformanceProfiler.mark("analytics-start");
        analytics.trackPageView("app_start"); // Initialize database tables (gracefully handle failures)
        PerformanceProfiler.measure("analytics-init", "analytics-start");

        try {
          PerformanceProfiler.mark("database-init-start");
          await databaseService.initializeTables();
          PerformanceProfiler.measure("database-init", "database-init-start");
        } catch (error) {
          console.warn(
            "Database initialization failed, continuing with localStorage fallback:",
            error
          );
        }

        // UTM promo service is automatically initialized on import

        // Start preloading components after initial load
        PerformanceProfiler.mark("preload-start");
        preloadCriticalComponents();
        PerformanceProfiler.measure("preload-components", "preload-start");

        // Track bundle performance in development
        if (process.env.NODE_ENV === "development") {
          setTimeout(trackBundleSize, 1000);
        }

        PerformanceProfiler.measure("total-app-init", "app-init-start");
        setIsInitializing(false);
      } catch (error) {
        console.error("App initialization error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        setInitError(errorMessage);
        analytics.trackError("app_init_error", errorMessage);
        setIsInitializing(false);
      }
    };

    initializeApp();
  }, []);

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Initializing Eclipse Booking System...
          </p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Initialization Error
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <GooglePlacesProvider
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
          >
            <BookingProvider>
              <Router>
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
                  <Header />
                  <PromotionalBannerComponent className="sticky top-0 z-40" />
                  <main className="container mx-auto px-4 py-8">
                    <Suspense
                      fallback={
                        <div className="flex justify-center items-center min-h-64">
                          <Spinner size="lg" />
                        </div>
                      }
                    >
                      <Routes>
                        <Route path="/" element={<ContactForm />} />
                        <Route path="/contact" element={<ContactForm />} />
                        <Route
                          path="/measurement"
                          element={<MeasurementForm />}
                        />
                        <Route path="/files" element={<FileUploadPage />} />
                        <Route
                          path="/estimate"
                          element={<PriceEstimationPage />}
                        />
                        <Route path="/booking" element={<BookingCalendar />} />{" "}
                        <Route
                          path="/confirmation"
                          element={<ConfirmationPage />}
                        />
                        <Route
                          path="/reschedule"
                          element={<RescheduleBookingPage />}
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_SETTINGS_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="settings"
                            >
                              <SettingsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/trak-settings"
                          element={<TrakSettingsPanel />}
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_DASHBOARD_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="dashboard"
                            >
                              <AnalyticsDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/leads"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_DASHBOARD_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="leads"
                            >
                              <LeadsDashboard />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Suspense>
                  </main>
                </div>
              </Router>
            </BookingProvider>
          </GooglePlacesProvider>{" "}
        </ThemeProvider>
      </QueryClientProvider>

      {/* Session monitoring for timeout and expiry handling */}
      <SessionMonitor
        showNotifications={true}
        onSessionWarning={(minutesLeft) => {
          console.log(`⏰ Session warning: ${minutesLeft} minutes remaining`);
          analytics.track("session_warning_shown", "session_management", {
            minutesLeft,
          });
        }}
        onSessionExpiry={() => {
          console.log("🔄 Session expired - handled by SessionMonitor");
          analytics.track("session_expired_handled", "session_management", {
            timestamp: new Date().toISOString(),
          });
        }}
      />

      <SpeedInsights />
    </ErrorBoundary>
  );
}

export default App;
